// Software licences for the Stark VMS, the Edge Recording Manager and the
// POS Bridge -- ADMIN only, on its own page (not part of the store forms).
//
// A licence = one product for one customer (a store, a store owner, or our
// own company), for a length of time, on N PCs. The customer types the
// licence key into the program once (internet needed once); after that the
// program checks its licence on its own and checks in with this server when
// it is online. Auto-renew: when the end date passes and the store is still
// active here, the licence is extended by itself.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Cctv, MonitorPlay, Radar, ScanBarcode, Store, Users } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import Sidebar from '../Component/Sidebar';
import '../CSS/LicenseManagement.css';

// Each program has its own licence form: who it can be for, and what it can
// be limited to (the program obeys these limits). 0 = no limit.
const PRODUCTS = [
  { id: 'vms', label: 'Stark VMS', short: 'VMS', Icon: Cctv,
    what: 'Records the cameras in one store.',
    holders: ['store'],
    pcs: { label: 'Recording PCs', hint: 'Usually 1: the PC in the store that records.' },
    limits: 'cameras, viewing from other PCs, POS, clip export' },
  { id: 'erm', label: 'Edge Recording Manager', short: 'Edge Recorder', Icon: MonitorPlay,
    what: 'Watches live video and playback from stores, from anywhere.',
    holders: ['owner', 'store', 'company'],
    pcs: { label: 'Viewing PCs', hint: 'How many PCs may run the Edge Recorder with this licence.' },
    limits: 'how many stores it may watch' },
  { id: 'pos_bridge', label: 'POS Bridge', short: 'POS Bridge', Icon: ScanBarcode,
    what: 'Reads the registers of one store and sends the sales to the VMS.',
    holders: ['store'],
    pcs: { label: 'Bridge PCs', hint: 'Usually 1: the PC the registers are wired to.' },
    limits: 'how many registers it reads' },
  { id: 'center', label: 'Stark Center', short: 'Center', Icon: Radar,
    what: 'Alarm monitoring for many stores at once. Stores connect to it themselves with its code.',
    holders: ['owner', 'company'],
    pcs: { label: 'Monitoring PCs', hint: 'How many operator PCs may run Center with this licence.' },
    limits: 'how many stores can connect to it' },
];
const productOf = (id) => PRODUCTS.find((p) => p.id === id) || PRODUCTS[0];
const HOLDER = {
  store: { label: 'One store', Icon: Store },
  owner: { label: 'A store owner', sub: 'all their stores', Icon: Users },
  company: { label: 'Our company', sub: 'every store', Icon: Building2 },
};
const productLabel = (id) => PRODUCTS.find((p) => p.id === id)?.label || id;

// The limits of a licence in a few words (list + details)
function allowsText(l) {
  const f = l.effective_features || l.features || {};
  const n = (v, one, many) => `${v} ${v === 1 ? one : many}`;
  if (l.product === 'vms') {
    const off = [!f.remote_access && 'no remote viewing', !f.pos && 'no POS', !f.exports && 'no export'].filter(Boolean);
    return [f.max_cameras ? `Up to ${n(f.max_cameras, 'camera', 'cameras')}` : 'Any number of cameras',
      f.stark_connect && 'Stark Connect', ...off].filter(Boolean).join(' · ');
  }
  if (l.product === 'erm') return f.max_stores ? `Up to ${n(f.max_stores, 'store', 'stores')}` : 'All the stores it covers';
  if (l.product === 'center') return f.max_stores ? `Up to ${n(f.max_stores, 'store', 'stores')}` : 'All the stores it covers';
  if (l.product === 'pos_bridge') return f.max_registers ? `Up to ${n(f.max_registers, 'register', 'registers')}` : 'Any number of registers';
  return '';
}

// The stores a licence covers (a store, an owner's stores, or all of them)
function coveredStores(holderType, storeId, ownerId, stores) {
  if (holderType === 'store') return stores.filter((s) => String(s.id) === String(storeId));
  if (holderType === 'owner') return ownerId ? stores.filter((s) => String(s.owner) === String(ownerId)) : [];
  return stores.filter((s) => s.is_active !== false);
}
const STATE_LABEL = { active: 'Active', expired: 'Expired', suspended: 'Paused', revoked: 'Cancelled' };
const LENGTHS = [
  { days: 30, label: '1 month' },
  { days: 90, label: '3 months' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' },
  { days: 730, label: '2 years' },
];
const EVENT_LABEL = {
  created: 'Created', edited: 'Edited', activated: 'Activated on a PC', auto_renewed: 'Renewed automatically',
  renewed: 'Renewed', suspended: 'Paused', resumed: 'Resumed', revoked: 'Cancelled', key_changed: 'New key',
  pc_removed: 'PC removed', licence_file: 'Licence file made',
};

// licences: stark_connect is off by default (the server treats a licence without it as off too)
export const FEATURE_DEFAULTS = {
  vms: { max_cameras: 0, remote_access: true, pos: true, exports: true, stark_connect: false },
  erm: { max_stores: 0 },
  pos_bridge: { max_registers: 0 },
  center: { max_stores: 0 },
};
const emptyForm = (product = 'vms') => ({
  product, holder_type: productOf(product).holders[0], store: '', owner: '', label: '',
  starts_on: new Date().toISOString().slice(0, 10), length_days: 365, ends_on: '',
  auto_renew: true, renew_days: 365, grace_days: 30, max_activations: 1,
  features: { ...FEATURE_DEFAULTS[product] },
  notes: '',
});

const fmtDate = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(undefined,
  { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined,
  { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function errText(err, fallback) {
  const d = err?.response?.data;
  if (!d) return fallback;
  if (typeof d === 'string') return fallback;
  if (d.detail) return d.detail;
  return Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : v}`).join('; ') || fallback;
}

function ProductBadge({ id }) {
  const p = productOf(id);
  return <span className={`lic-badge product prod-${p.id}`}><p.Icon size={13} strokeWidth={2} /> {p.short}</span>;
}

function StateBadge({ lic }) {
  const soon = lic.state === 'active' && lic.days_left <= 30 && !lic.auto_renew;
  return (
    <span className={`lic-badge ${soon ? 'soon' : lic.state}`}>
      {soon ? `Ends in ${lic.days_left} d` : STATE_LABEL[lic.state] || lic.state}
    </span>
  );
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── create / edit ────────────────────────────────────────────────────────────
// Step 1 (new licence only): pick the program. Step 2: that program's form --
// only the holders it can have, its own limits, its own words.
function ProgramPicker({ onPick }) {
  return (
    <div className="lic-programs">
      {PRODUCTS.map((p) => (
        <button type="button" key={p.id} className={`lic-program prod-${p.id}`} onClick={() => onPick(p.id)}>
          <span className="lic-program-icon"><p.Icon size={22} strokeWidth={1.8} /></span>
          <span className="lic-program-name">{p.label}</span>
          <span className="lic-program-what">{p.what}</span>
          <span className="lic-program-meta">
            <span>For {p.holders.map((h) => HOLDER[h].label.toLowerCase()).join(', ')}</span>
            <span>Limits: {p.limits}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function Toggle({ checked, onChange, title, sub }) {
  return (
    <label className="lic-toggle">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="lic-toggle-track" aria-hidden="true"><span /></span>
      <span className="lic-toggle-text"><b>{title}</b>{sub && <small>{sub}</small>}</span>
    </label>
  );
}

function Limit({ id, label, value, onChange, unit, hint, warn }) {
  return (
    <div className="lic-limit">
      <label className="lic-label" htmlFor={id}>{label}</label>
      <div className="lic-limit-row">
        <input id={id} type="number" min="0" className="lic-input lic-limit-input" value={value}
               onChange={(e) => onChange(e.target.value)} />
        <span className="lic-limit-unit">{Number(value) > 0 ? unit : `${unit} — no limit`}</span>
      </div>
      {hint && <span className="lic-hint">{hint}</span>}
      {warn && <div className="lic-warn">{warn}</div>}
    </div>
  );
}

function Coverage({ product, covered }) {
  // Stark Center: stores connect to it themselves (its code + an ID per store), not by owner
  if (product === 'vms' || product === 'pos_bridge' || product === 'center') return null;
  const show = covered.slice(0, 8);
  return (
    <div className="lic-coverage">
      <div className="lic-coverage-head">
        {product === 'erm' ? 'Can watch' : 'Monitors'} <b>{covered.length}</b> {covered.length === 1 ? 'store' : 'stores'}
        {covered.length === 0 && ' — pick who it is for'}
      </div>
      {covered.length > 0 && (
        <div className="lic-coverage-list">
          {show.map((s) => <span key={s.id} className={`lic-chip${s.is_active === false ? ' off' : ''}`}>{s.store_name}</span>)}
          {covered.length > show.length && <span className="lic-chip more">+{covered.length - show.length} more</span>}
        </div>
      )}
    </div>
  );
}

function LicenseForm({ initial, product, stores, owners, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [f, setF] = useState(() => (editing ? {
    ...emptyForm(initial.product), ...initial,
    store: initial.store || '', owner: initial.owner || '',
    features: { ...FEATURE_DEFAULTS[initial.product], ...(initial.features || {}) },
  } : (product ? emptyForm(product) : null)));
  const [registersTouched, setRegistersTouched] = useState(editing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  const setFeat = (k, v) => setF((o) => ({ ...o, features: { ...o.features, [k]: v } }));

  const ownerName = (o) => {
    const u = o.user || {};
    return `${[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || `Owner ${o.id}`}`;
  };
  const covered = useMemo(() => (f ? coveredStores(f.holder_type, f.store, f.owner, stores) : []),
    [f, stores]);

  // POS Bridge: start from the number of registers the store has on the portal
  const pickStore = (id) => {
    setF((o) => {
      const next = { ...o, store: id };
      const st = stores.find((s) => String(s.id) === String(id));
      if (o.product === 'pos_bridge' && !registersTouched && st?.pos_qty) {
        next.features = { ...o.features, max_registers: st.pos_qty };
      }
      return next;
    });
  };

  if (!f) {
    return (
      <div className="lic-modal-backdrop" onClick={onClose}>
        <div className="lic-modal lic-modal-wide" onClick={(e) => e.stopPropagation()}>
          <div className="lic-modal-header">
            <h3>New licence — which program?</h3>
            <button className="lic-close" onClick={onClose} aria-label="Close">×</button>
          </div>
          <div className="lic-modal-body">
            <ProgramPicker onPick={(id) => setF(emptyForm(id))} />
          </div>
        </div>
      </div>
    );
  }

  const prod = productOf(f.product);
  const legacyHolder = !prod.holders.includes(f.holder_type);   // an older licence made before the per-program rules
  const holders = legacyHolder ? [...prod.holders, f.holder_type] : prod.holders;
  const storeLimitWarn = (max) => (Number(max) > 0 && covered.length > Number(max)
    ? `It covers ${covered.length} stores but may ${f.product === 'erm' ? 'watch' : 'monitor'} only ${max}. `
      + 'The program shows the first ones added and says the rest are not in its licence.'
    : '');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    const num = (v) => Math.max(0, Number(v) || 0);
    const ft = f.features;
    const features = {
      vms: { max_cameras: num(ft.max_cameras), remote_access: !!ft.remote_access, pos: !!ft.pos, exports: !!ft.exports,
             stark_connect: !!ft.stark_connect },
      erm: { max_stores: num(ft.max_stores) },
      center: { max_stores: num(ft.max_stores) },
      pos_bridge: { max_registers: num(ft.max_registers) },
    }[f.product] || {};
    const body = {
      product: f.product, holder_type: f.holder_type, label: f.label.trim(),
      store: f.holder_type === 'store' ? (Number(f.store) || null) : null,
      owner: f.holder_type === 'owner' ? (Number(f.owner) || null) : null,
      auto_renew: !!f.auto_renew, renew_days: Number(f.renew_days) || 1, grace_days: Number(f.grace_days) || 1,
      max_activations: Number(f.max_activations) || 1, features, notes: f.notes,
    };
    if (editing) {
      body.ends_on = f.ends_on;
      if (f.holder_type === initial.holder_type) delete body.holder_type;   // unchanged: keep an older holder
    } else { body.starts_on = f.starts_on; body.length_days = Number(f.length_days) || 1; }
    try {
      const r = editing
        ? await axiosInstance.patch(`/api/licenses/${initial.id}/`, body)
        : await axiosInstance.post('/api/licenses/', body);
      onSaved(r.data, editing);
    } catch (err) {
      setError(errText(err, 'Could not save the licence.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lic-modal-backdrop" onClick={onClose}>
      <div className={`lic-modal lic-modal-wide prod-${f.product}`} onClick={(e) => e.stopPropagation()}>
        <div className="lic-modal-header lic-form-head">
          <span className="lic-program-icon"><prod.Icon size={20} strokeWidth={1.8} /></span>
          <div className="lic-form-head-text">
            <h3>{editing ? `Edit ${prod.label} licence` : `New ${prod.label} licence`}</h3>
            <span>{prod.what}</span>
          </div>
          {!editing && <button type="button" className="lic-link" onClick={() => setF(null)}>Change program</button>}
          <button className="lic-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form onSubmit={submit}>
          <div className="lic-modal-body">
            {error && <div className="lic-error">{error}</div>}

            <section className="lic-section">
              <h4 className="lic-section-title">Who it's for</h4>
              {holders.length > 1 && (
                <div className="lic-holders" role="radiogroup" aria-label="Who it's for">
                  {holders.map((h) => {
                    const H = HOLDER[h];
                    return (
                      <button type="button" role="radio" aria-checked={f.holder_type === h} key={h}
                              className={`lic-holder${f.holder_type === h ? ' on' : ''}`}
                              onClick={() => set('holder_type', h)}>
                        <H.Icon size={16} strokeWidth={1.9} />
                        <span><b>{H.label}</b>{H.sub && <small>{H.sub}</small>}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {f.holder_type === 'store' && (
                <div className="lic-form-group">
                  <label className="lic-label" htmlFor="lic-store">{holders.length > 1 ? 'Store' : 'The store it is installed in'}</label>
                  <select id="lic-store" className="lic-select" value={f.store} onChange={(e) => pickStore(e.target.value)} required>
                    <option value="">Pick a store…</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>{s.store_name}{s.store_city ? `, ${s.store_city}` : ''}{s.is_active === false ? ' (inactive)' : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              {f.holder_type === 'owner' && (
                <div className="lic-form-group">
                  <label className="lic-label" htmlFor="lic-owner">Store owner</label>
                  <select id="lic-owner" className="lic-select" value={f.owner} onChange={(e) => set('owner', e.target.value)} required>
                    <option value="">Pick an owner…</option>
                    {owners.map((o) => <option key={o.id} value={o.id}>{ownerName(o)}</option>)}
                  </select>
                </div>
              )}
              {legacyHolder && f.holder_type === initial?.holder_type && (
                <div className="lic-info">Made before each program had its own rules: a {prod.label} licence is now for {prod.holders.map((h) => HOLDER[h].label.toLowerCase()).join(' or ')}.</div>
              )}
              <Coverage product={f.product} covered={covered} />
              {f.product === 'center' && (
                <div className="lic-info">
                  Stores aren't picked here. Each store connects to this Center itself, with the Center code and the ID
                  and password the Center makes for it (like GeoVision Center V2).
                </div>
              )}
              <div className="lic-form-group">
                <label className="lic-label" htmlFor="lic-label">Label <small>(optional, only we see it)</small></label>
                <input id="lic-label" className="lic-input" value={f.label} onChange={(e) => set('label', e.target.value)}
                       placeholder={{ vms: 'e.g. Back office recorder', erm: "e.g. Owner's laptop", pos_bridge: 'e.g. Counter PC', center: 'e.g. Night shift desk' }[f.product]}
                       maxLength={200} />
              </div>
            </section>

            <section className="lic-section">
              <h4 className="lic-section-title">What it allows</h4>
              {f.product === 'vms' && (
                <>
                  <Limit id="lic-cams" label="Cameras" unit="cameras" value={f.features.max_cameras}
                         onChange={(v) => setFeat('max_cameras', v)}
                         hint="Cameras beyond this number can't be added in the VMS." />
                  <div className="lic-toggles">
                    <Toggle checked={f.features.remote_access} onChange={(v) => setFeat('remote_access', v)}
                            title="Viewing from other PCs and phones" sub="Edge Recorder, Center and the phone app, by the store's address" />
                    <Toggle checked={f.features.stark_connect} onChange={(v) => setFeat('stark_connect', v)}
                            title="Stark Connect"
                            sub="Reach this store through our relay when its router can't be opened. Needs viewing from other PCs too." />
                    <Toggle checked={f.features.pos} onChange={(v) => setFeat('pos', v)}
                            title="POS transactions" sub="Receipts over the cameras, search by sale" />
                    <Toggle checked={f.features.exports} onChange={(v) => setFeat('exports', v)}
                            title="Export clips" sub="Save recordings as video files" />
                  </div>
                </>
              )}
              {f.product === 'erm' && (
                <Limit id="lic-stores" label="Stores it may watch" unit="stores" value={f.features.max_stores}
                       onChange={(v) => setFeat('max_stores', v)}
                       hint="Counts every store added in the Edge Recorder, by address or Stark Connect."
                       warn={storeLimitWarn(f.features.max_stores)} />
              )}
              {f.product === 'center' && (
                <Limit id="lic-stores" label="Stores that can connect" unit="stores" value={f.features.max_stores}
                       onChange={(v) => setFeat('max_stores', v)}
                       hint="Stores connect to this Center themselves, with its Center code and the ID it gives each store. A store past this number is refused." />
              )}
              {f.product === 'pos_bridge' && (
                <Limit id="lic-regs" label="Registers" unit="registers" value={f.features.max_registers}
                       onChange={(v) => { setRegistersTouched(true); setFeat('max_registers', v); }}
                       hint={(() => {
                         const st = stores.find((s) => String(s.id) === String(f.store));
                         return `Registers beyond this number are still read but not sent to the VMS, Geovision or clips.${st?.pos_qty ? ` ${st.store_name} has ${st.pos_qty} on the portal.` : ''}`;
                       })()} />
              )}
              <div className="lic-limit">
                <label className="lic-label" htmlFor="lic-pcs">{prod.pcs.label}</label>
                <div className="lic-limit-row">
                  <input id="lic-pcs" type="number" min="1" className="lic-input lic-limit-input" value={f.max_activations}
                         onChange={(e) => set('max_activations', e.target.value)} />
                  <span className="lic-limit-unit">{Number(f.max_activations) === 1 ? 'PC' : 'PCs'}</span>
                </div>
                <span className="lic-hint">{prod.pcs.hint}</span>
              </div>
            </section>

            <section className="lic-section">
              <h4 className="lic-section-title">How long</h4>
              {editing ? (
                <div className="lic-form-row">
                  <div className="lic-form-group">
                    <label className="lic-label" htmlFor="lic-ends">Paid until (last day)</label>
                    <input id="lic-ends" type="date" className="lic-input" value={f.ends_on} onChange={(e) => set('ends_on', e.target.value)} required />
                  </div>
                </div>
              ) : (
                <>
                  <div className="lic-chips">
                    {LENGTHS.map((l) => (
                      <button type="button" key={l.days}
                              className={`lic-btn lic-btn-small ${Number(f.length_days) === l.days ? 'lic-btn-primary' : 'lic-btn-light'}`}
                              onClick={() => setF((o) => ({ ...o, length_days: l.days, renew_days: l.days }))}>{l.label}</button>
                    ))}
                  </div>
                  <div className="lic-form-row">
                    <div className="lic-form-group">
                      <label className="lic-label" htmlFor="lic-starts">Starts on</label>
                      <input id="lic-starts" type="date" className="lic-input" value={f.starts_on} onChange={(e) => set('starts_on', e.target.value)} required />
                    </div>
                    <div className="lic-form-group">
                      <label className="lic-label" htmlFor="lic-len">Length (days)</label>
                      <input id="lic-len" type="number" min="1" className="lic-input" value={f.length_days}
                             onChange={(e) => set('length_days', e.target.value)} required />
                    </div>
                  </div>
                </>
              )}
              <Toggle checked={f.auto_renew} onChange={(v) => set('auto_renew', v)}
                      title="Renew automatically"
                      sub={f.holder_type === 'company' ? 'Our own licence: renews every time'
                        : `While ${f.holder_type === 'store' ? 'the store is active' : 'the owner has an active store'} on this portal. Deactivating ${f.holder_type === 'store' ? 'the store' : 'all their stores'} stops it.`} />
              <div className="lic-form-row">
                {f.auto_renew && (
                  <div className="lic-form-group">
                    <label className="lic-label" htmlFor="lic-renew">Each renewal adds (days)</label>
                    <input id="lic-renew" type="number" min="1" className="lic-input" value={f.renew_days} onChange={(e) => set('renew_days', e.target.value)} />
                  </div>
                )}
                <div className="lic-form-group">
                  <label className="lic-label" htmlFor="lic-grace">Works offline for (days)</label>
                  <input id="lic-grace" type="number" min="1" className="lic-input" value={f.grace_days} onChange={(e) => set('grace_days', e.target.value)} />
                  <span className="lic-hint">How long a PC keeps working without reaching our server. A paused or cancelled licence reaches an offline PC only after this.</span>
                </div>
              </div>
            </section>

            <div className="lic-form-group">
              <label className="lic-label" htmlFor="lic-notes">Notes <small>(only we see these)</small></label>
              <textarea id="lic-notes" className="lic-input" rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>
          </div>
          <div className="lic-modal-footer">
            <button type="button" className="lic-btn lic-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="lic-btn lic-btn-primary" disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save' : `Create ${prod.short} licence`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── licence file for an offline PC ───────────────────────────────────────────
function OfflineFile({ license, licenses, onDone }) {
  const [code, setCode] = useState('');
  const [pick, setPick] = useState(license?.id || '');
  const [needPick, setNeedPick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const make = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await axiosInstance.post('/api/licenses/offline-activate/',
        { request_code: code.trim(), license_id: pick || undefined });
      downloadText(r.data.filename, r.data.token);
      setMsg({ ok: true, text: `Licence file ${r.data.filename} downloaded. Give it to the store; they open it in the program (Licence → Load licence file).` });
      setCode(''); setNeedPick(false);
      onDone?.(r.data.license_id);
    } catch (err) {
      if (err?.response?.data?.need_license) setNeedPick(true);
      setMsg({ ok: false, text: errText(err, 'Could not make the licence file.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lic-form-group">
      <span className="lic-hint">
        For a PC without internet: the program shows a request code (Licence → No internet). Paste it here to download
        its licence file.
      </span>
      <textarea className="lic-textarea" placeholder="SVLREQ.…" value={code} onChange={(e) => setCode(e.target.value)} />
      {(needPick || !license) && (
        <select className="lic-select" value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">Licence (only needed if the code has no key)…</option>
          {licenses.map((l) => <option key={l.id} value={l.id}>{productLabel(l.product)} — {l.holder_name} ({l.key})</option>)}
        </select>
      )}
      <div><button className="lic-btn lic-btn-primary lic-btn-small" disabled={busy || !code.trim()} onClick={make}>
        {busy ? 'Making…' : 'Download licence file'}</button></div>
      {msg && <div className={msg.ok ? 'lic-success' : 'lic-error'}>{msg.text}</div>}
    </div>
  );
}

// ─── detail drawer ────────────────────────────────────────────────────────────
function LicenseDetail({ id, licenses, onClose, onChanged, onEdit }) {
  const [lic, setLic] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/api/licenses/${id}/`);
      setLic(r.data); setError('');
    } catch (err) {
      setError(errText(err, 'Could not load the licence.'));
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (name, body, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(name); setError('');
    try {
      await axiosInstance.post(`/api/licenses/${id}/${name}/`, body || {});
      await load(); onChanged();
    } catch (err) {
      setError(errText(err, 'That did not work.'));
    } finally {
      setBusy('');
    }
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(lic.key); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { window.prompt('Licence key:', lic.key); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this licence? It was never activated.')) return;
    try { await axiosInstance.delete(`/api/licenses/${id}/`); onChanged(); onClose(); }
    catch (err) { setError(errText(err, 'Could not delete.')); }
  };

  const feats = lic?.effective_features || {};
  const activePcs = (lic?.activations || []).filter((a) => a.is_active);

  return (
    <>
      <div className="lic-drawer-backdrop" onClick={onClose} />
      <aside className="lic-drawer" aria-label="Licence details">
        <div className="lic-drawer-head">
          <div>
            <h3 className="lic-drawer-title">{lic ? lic.holder_name : 'Licence'}</h3>
            {lic && <div className="lic-sub"><ProductBadge id={lic.product} /> {lic.label}</div>}
          </div>
          <button className="lic-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="lic-drawer-body">
          {error && <div className="lic-error">{error}</div>}
          {!lic ? <div className="lic-loading">Loading…</div> : (
            <>
              <div className="lic-card">
                <h4>Licence key</h4>
                <div className="lic-key-box">
                  <span className="lic-mono">{lic.key}</span>
                  <button className="lic-btn lic-btn-light lic-btn-small" onClick={copyKey}>{copied ? 'Copied' : 'Copy'}</button>
                </div>
                <div className="lic-hint" style={{ marginTop: 8 }}>
                  The store types this into the program once (internet needed once). A new key stops the old one from
                  activating more PCs; PCs already activated keep working.
                </div>
                <div className="lic-actions" style={{ marginTop: 10 }}>
                  <button className="lic-btn lic-btn-light lic-btn-small" disabled={!!busy}
                          onClick={() => act('regenerate-key', null, 'Make a new key? The old key will no longer activate new PCs.')}>New key</button>
                </div>
              </div>

              <div className="lic-card">
                <h4>Status</h4>
                <dl className="lic-kv">
                  <dt>State</dt><dd><StateBadge lic={lic} />{lic.status_reason ? ` ${lic.status_reason}` : ''}</dd>
                  <dt>Paid until</dt><dd>{fmtDate(lic.ends_on)} ({lic.days_left >= 0 ? `${lic.days_left} days left` : `${-lic.days_left} days ago`})</dd>
                  <dt>Auto-renew</dt><dd>{lic.auto_renew ? `On, +${lic.renew_days} days each time${lic.holder_active ? '' : ' — paused: customer inactive'}` : 'Off'}</dd>
                  <dt>Works offline for</dt><dd>{lic.grace_days} days</dd>
                  <dt>Started</dt><dd>{fmtDate(lic.starts_on)}</dd>
                  {lic.last_renewed_at && (<><dt>Last renewed</dt><dd>{fmtDateTime(lic.last_renewed_at)}</dd></>)}
                </dl>
                <div className="lic-actions" style={{ marginTop: 14 }}>
                  <button className="lic-btn lic-btn-success lic-btn-small" disabled={!!busy || lic.status === 'revoked'}
                          onClick={() => {
                            const d = window.prompt('Extend by how many days?', String(lic.renew_days));
                            if (d) act('renew', { days: Number(d) });
                          }}>Renew now…</button>
                  {lic.status === 'active' && (
                    <button className="lic-btn lic-btn-warning lic-btn-small" disabled={!!busy}
                            onClick={() => { const r = window.prompt('Pause this licence. Message shown to the store:', 'Payment due'); if (r !== null) act('suspend', { reason: r }); }}>Pause…</button>
                  )}
                  {lic.status === 'suspended' && (
                    <button className="lic-btn lic-btn-success lic-btn-small" disabled={!!busy} onClick={() => act('resume')}>Resume</button>
                  )}
                  {lic.status !== 'revoked' && (
                    <button className="lic-btn lic-btn-danger lic-btn-small" disabled={!!busy}
                            onClick={() => { const r = window.prompt('Cancel this licence for good. Message shown to the store:', 'Subscription cancelled'); if (r !== null) act('revoke', { reason: r }); }}>Cancel licence…</button>
                  )}
                  <button className="lic-btn lic-btn-light lic-btn-small" onClick={() => onEdit(lic)}>Edit</button>
                  {(lic.activations || []).length === 0 && (
                    <button className="lic-btn lic-btn-light lic-btn-small" onClick={remove}>Delete</button>
                  )}
                </div>
                <div className="lic-hint" style={{ marginTop: 10 }}>
                  Paused / cancelled: PCs that are online switch to limited mode within a few hours; offline PCs after at
                  most {lic.grace_days} days. Recording is never stopped.
                </div>
              </div>

              <div className="lic-card">
                <h4>What it allows</h4>
                <dl className="lic-kv">
                  <dt>{productOf(lic.product).pcs.label}</dt><dd>{activePcs.length} of {lic.max_activations} used</dd>
                  {(lic.product === 'erm' || lic.product === 'center') && (<>
                    <dt>{lic.product === 'erm' ? 'Stores it may watch' : 'Stores that can connect'}</dt>
                    <dd>{feats.max_stores ? `Up to ${feats.max_stores}` : 'All the stores it covers'}</dd>
                  </>)}
                  {lic.product === 'pos_bridge' && (<>
                    <dt>Registers</dt><dd>{feats.max_registers ? `Up to ${feats.max_registers}` : 'No limit'}</dd>
                  </>)}
                  {lic.product === 'vms' && (<>
                    <dt>Cameras</dt><dd>{feats.max_cameras ? `Up to ${feats.max_cameras}` : 'No limit'}</dd>
                    <dt>Viewing from other PCs</dt><dd>{feats.remote_access ? 'Yes' : 'No'}</dd>
                    <dt>Stark Connect</dt><dd>{feats.stark_connect ? 'Yes' : 'No'}</dd>
                    <dt>POS transactions</dt><dd>{feats.pos ? 'Yes' : 'No'}</dd>
                    <dt>Export clips</dt><dd>{feats.exports ? 'Yes' : 'No'}</dd>
                  </>)}
                </dl>
                {lic.notes && <div className="lic-hint" style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>{lic.notes}</div>}
              </div>

              <div className="lic-card">
                <h4>PCs</h4>
                {(lic.activations || []).length === 0 && <div className="lic-hint">Not activated on any PC yet.</div>}
                {(lic.activations || []).map((a) => (
                  <div key={a.id} className={`lic-pc${a.is_active ? '' : ' gone'}`}>
                    <div>
                      <div><b>{a.machine_name || 'PC'}</b> {a.offline && <span className="lic-badge suspended">licence file</span>}</div>
                      <div className="lic-sub">
                        {a.is_active ? `Last check-in ${fmtDateTime(a.last_check_in)}` : `Removed ${fmtDateTime(a.deactivated_at)}`}
                        {a.app_version ? ` · v${a.app_version}` : ''}{a.last_ip ? ` · ${a.last_ip}` : ''}
                      </div>
                      <div className="lic-sub lic-mono">{a.machine_id}</div>
                    </div>
                    {a.is_active && (
                      <button className="lic-btn lic-btn-light lic-btn-small" disabled={!!busy}
                              onClick={() => act('deactivate-pc', { activation_id: a.id },
                                `Remove ${a.machine_name || 'this PC'}? Its place is freed for another PC (moving the licence); this PC stops at its next check-in.`)}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="lic-card">
                <h4>Licence file (PC without internet)</h4>
                <OfflineFile license={lic} licenses={licenses} onDone={() => { load(); onChanged(); }} />
              </div>

              <div className="lic-card">
                <h4>History</h4>
                {(lic.events || []).map((e) => (
                  <div key={e.id} className="lic-event">
                    <div className="lic-event-time">{fmtDateTime(e.at)}</div>
                    <div>
                      <span className="lic-event-kind">{EVENT_LABEL[e.kind] || e.kind}</span>
                      {e.by_username ? ` by ${e.by_username}` : ''}
                      {e.detail && <div className="lic-sub">{e.detail}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────
function LicenseManagement() {
  const [licenses, setLicenses] = useState([]);
  const [stores, setStores] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filters, setFilters] = useState({ product: '', state: '', search: '' });   // product = the tab
  const [quick, setQuick] = useState('');            // stat-card filter
  const [form, setForm] = useState(null);            // null | {} (new) | licence (edit)
  const [openId, setOpenId] = useState(null);
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [ver, setVer] = useState(0);                 // reloads the open licence after an edit

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/api/licenses/');
      setLicenses(r.data.results || r.data);
      setError('');
    } catch (err) {
      setError(errText(err, 'Failed to load licences.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    axiosInstance.get('/api/stores/').then((r) => setStores(r.data.results || r.data)).catch(() => {});
    axiosInstance.get('/api/store-owners/').then((r) => setOwners(r.data.results || r.data)).catch(() => {});
  }, [load]);

  const inTab = useMemo(() => licenses.filter((l) => !filters.product || l.product === filters.product),
    [licenses, filters.product]);
  const stats = useMemo(() => ({
    active: inTab.filter((l) => l.state === 'active').length,
    soon: inTab.filter((l) => l.state === 'active' && !l.auto_renew && l.days_left <= 30).length,
    expired: inTab.filter((l) => l.state === 'expired').length,
    paused: inTab.filter((l) => l.state === 'suspended' || l.state === 'revoked').length,
  }), [inTab]);
  const tabProduct = filters.product ? productOf(filters.product) : null;

  const shown = useMemo(() => {
    const s = filters.search.trim().toLowerCase();
    return licenses.filter((l) => {
      if (filters.product && l.product !== filters.product) return false;
      if (filters.state && l.state !== filters.state) return false;
      if (quick === 'active' && l.state !== 'active') return false;
      if (quick === 'soon' && !(l.state === 'active' && !l.auto_renew && l.days_left <= 30)) return false;
      if (quick === 'expired' && l.state !== 'expired') return false;
      if (quick === 'paused' && !(l.state === 'suspended' || l.state === 'revoked')) return false;
      if (s && !`${l.key} ${l.holder_name} ${l.label} ${l.notes}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [licenses, filters, quick]);

  const saved = (lic, editing) => {
    setForm(null);
    setVer((v) => v + 1);
    load();
    if (!editing) {
      setNotice(`Licence created. Key: ${lic.key}`);
      setOpenId(lic.id);
    }
  };

  return (
    <div className="index-box">
      <Sidebar />
      <div className="lic-container">
        <div className="lic-header">
          <div>
            <h1 className="lic-header-title">Software Licences</h1>
            <p className="lic-header-sub">Stark VMS, Edge Recording Manager, POS Bridge and Stark Center. Only admins see this page.</p>
          </div>
          <div className="lic-header-actions">
            <button className="lic-btn lic-btn-light" onClick={() => setOfflineOpen(true)}>Licence file for offline PC</button>
            <button className="lic-btn lic-btn-primary" onClick={() => setForm(tabProduct ? { product: tabProduct.id } : {})}>
              + New {tabProduct ? `${tabProduct.short} ` : ''}licence
            </button>
          </div>
        </div>

        {notice && <div className="lic-success" onClick={() => setNotice('')}>{notice}</div>}
        {error && <div className="lic-error">{error}</div>}

        <div className="lic-tabs" role="tablist" aria-label="Program">
          {[{ id: '', label: 'All programs' }, ...PRODUCTS].map((p) => {
            const n = p.id ? licenses.filter((l) => l.product === p.id).length : licenses.length;
            return (
              <button key={p.id || 'all'} role="tab" aria-selected={filters.product === p.id}
                      className={`lic-tab${p.id ? ` prod-${p.id}` : ''}${filters.product === p.id ? ' on' : ''}`}
                      onClick={() => setFilters({ ...filters, product: p.id })}>
                {p.Icon && <p.Icon size={16} strokeWidth={1.9} />}
                <span>{p.label}</span><span className="lic-tab-n">{n}</span>
              </button>
            );
          })}
        </div>

        <div className="lic-stats">
          {[['active', 'Active', stats.active], ['soon', 'Ending soon', stats.soon],
            ['expired', 'Expired', stats.expired], ['paused', 'Paused / cancelled', stats.paused]].map(([k, label, n]) => (
            <div key={k} className={`lic-stat-card ${k}${quick === k ? ' selected' : ''}`}
                 onClick={() => setQuick(quick === k ? '' : k)} role="button" tabIndex={0}>
              <div className="lic-stat-value">{n}</div>
              <div className="lic-stat-label">{label}</div>
            </div>
          ))}
        </div>

        <div className="lic-filters">
          <div className="lic-filter-group">
            <label className="lic-filter-label">State</label>
            <select className="lic-select" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })}>
              <option value="">All</option>
              {Object.entries(STATE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="lic-filter-group grow">
            <label className="lic-filter-label">Search</label>
            <input className="lic-input" placeholder="Store, owner, key, label…" value={filters.search}
                   onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
          </div>
        </div>

        {loading ? <div className="lic-loading">Loading licences…</div> : (
          <div className="lic-table-container">
            <table className="lic-table">
              <thead>
                <tr>
                  {!tabProduct && <th className="lic-th">Program</th>}
                  <th className="lic-th">For</th>
                  <th className="lic-th">{tabProduct ? { vms: 'Cameras & features', erm: 'Stores', pos_bridge: 'Registers', center: 'Stores' }[tabProduct.id] : 'Allows'}</th>
                  <th className="lic-th">Key</th>
                  <th className="lic-th">State</th>
                  <th className="lic-th">Paid until</th>
                  <th className="lic-th">Auto-renew</th>
                  <th className="lic-th">{tabProduct ? tabProduct.pcs.label : 'PCs'}</th>
                  <th className="lic-th">Last check-in</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={tabProduct ? 8 : 9} className="lic-td lic-empty">
                    {inTab.length ? 'No licences match.'
                      : tabProduct ? `No ${tabProduct.label} licences yet.` : 'No licences yet. Create one with "+ New licence".'}
                  </td></tr>
                )}
                {shown.map((l) => (
                  <tr key={l.id} className={`lic-row${l.state === 'revoked' ? ' dim' : ''}`} onClick={() => setOpenId(l.id)}>
                    {!tabProduct && <td className="lic-td"><ProductBadge id={l.product} /></td>}
                    <td className="lic-td">
                      <div>{l.holder_name}</div>
                      <div className="lic-sub">
                        {l.holder_type === 'store' ? 'Store' : l.holder_type === 'owner' ? 'Store owner' : 'Our company'}
                        {l.label ? ` · ${l.label}` : ''}{l.holder_active ? '' : ' · inactive'}
                      </div>
                    </td>
                    <td className="lic-td lic-allows">{allowsText(l)}</td>
                    <td className="lic-td lic-mono">{l.key}</td>
                    <td className="lic-td"><StateBadge lic={l} /></td>
                    <td className="lic-td lic-nowrap">
                      {fmtDate(l.ends_on)}
                      <div className="lic-sub">{l.days_left >= 0 ? `${l.days_left} days left` : `${-l.days_left} days ago`}</div>
                    </td>
                    <td className="lic-td">{l.auto_renew ? `+${l.renew_days} d` : 'Off'}</td>
                    <td className="lic-td lic-nowrap">{l.activations_used} / {l.max_activations}</td>
                    <td className="lic-td">{l.last_check_in ? fmtDateTime(l.last_check_in) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {form && (
        <LicenseForm initial={form.id ? form : null} product={form.id ? null : form.product} stores={stores} owners={owners}
                     onClose={() => setForm(null)} onSaved={saved} />
      )}
      {openId && (
        <LicenseDetail id={openId} licenses={licenses} onClose={() => setOpenId(null)} onChanged={load}
                       onEdit={(lic) => setForm(lic)} key={`${openId}-${ver}`} />
      )}
      {offlineOpen && (
        <div className="lic-modal-backdrop" onClick={() => setOfflineOpen(false)}>
          <div className="lic-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lic-modal-header">
              <h3>Licence file for a PC without internet</h3>
              <button className="lic-close" onClick={() => setOfflineOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="lic-modal-body">
              <OfflineFile license={null} licenses={licenses} onDone={() => load()} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LicenseManagement;
